import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Section, Eyebrow } from "@/components/Bits";
import { COMPANY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = ["General", "Spelling App", "Lesson Planner", "Resources"];

const inputClass =
  "w-full rounded-xl border border-charcoal/15 bg-white px-4 py-3.5 text-charcoal placeholder:text-charcoal/40 transition-colors duration-200 focus:border-purple focus:outline-none focus:ring-2 focus:ring-purple/20";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", topic: "General" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in your name, email, subject and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.email_delivered) {
        toast.success("Thanks — your message has been sent. We'll be in touch.");
      } else {
        toast.success("Thanks — your message has been received safely. We'll be in touch.");
      }
      setForm({ name: "", email: "", subject: "", message: "", topic: "General" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contact | FreeRange.Education" description="Questions about FreeRange.Education, our apps or the resources we're developing? Get in touch — we'd love to hear from you." />

      <Section className="pb-16 pt-32 md:pt-40">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-charcoal sm:text-6xl">
              Get in touch
            </h1>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-charcoal/75">
              Questions about FreeRange.Education, our apps or the resources we're developing? We'd love to hear from you.
            </p>
            <a
              href={`mailto:${COMPANY.email}`}
              className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-sand px-5 py-4 font-semibold text-purple transition-colors duration-200 hover:bg-sunny/40"
              data-testid="contact-email-link"
            >
              <Mail className="h-5 w-5" /> {COMPANY.email}
            </a>
            <p className="mt-8 text-sm text-charcoal/50">
              No account needed. Your enquiry is stored securely and sent to our inbox.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-[2rem] border border-charcoal/10 bg-white p-6 shadow-ambient md:p-9" data-testid="contact-form">
              <div className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-charcoal">Name</span>
                    <input className={inputClass} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-charcoal">Email</span>
                    <input type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email" />
                  </label>
                </div>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-charcoal">Subject</span>
                  <input className={inputClass} value={form.subject} onChange={set("subject")} placeholder="How can we help?" data-testid="contact-subject" />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-charcoal">
                    Product / topic <span className="font-normal text-charcoal/40">(optional)</span>
                  </span>
                  <select className={inputClass} value={form.topic} onChange={set("topic")} data-testid="contact-topic">
                    {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-sm font-semibold text-charcoal">Message</span>
                  <textarea rows={5} className={`${inputClass} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us a little more…" data-testid="contact-message" />
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-purple px-7 py-4 font-semibold text-cream transition-colors duration-300 hover:bg-purple-dark disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : "Send message"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </Section>
      <div className="pb-24" />
    </>
  );
}
